/**
 * Acerto da carga em três etapas — 1. informar o retorno, 2. confirmar a conferência,
 * 3. finalizar — com um estado só e uma ação principal por etapa. A etapa, o que falta e quem
 * pode fazer vêm da API (`settlement.stage` e `settlement.nextAction`): a tela não decide regra.
 *
 * - Retorno: a contagem física é sempre informada pelo operador (o esperado é só referência;
 *   "Confere" preenche uma linha por vez). Nada é gravado sozinho.
 * - Conferência: as quatro divergências (falta/sobra de mercadoria e de dinheiro), justificativa
 *   obrigatória quando há diferença.
 * - Finalização: o resumo do que vai acontecer, liberação de divergência e a finalização com
 *   uma chave de idempotência por intenção (duplo clique ou queda de conexão não repetem nada).
 */
import {
  PAYMENT_HANDLING_LABEL,
  SETTLEMENT_ORIGIN_LABEL,
  SETTLEMENT_REASON_MIN,
  compareMoney,
  formatMoney,
  toFormMoney,
  toFormNumber,
  toScaled,
  type EntryMode,
  type LoadDetail,
  type SettlementStage,
} from "@cafegestor/shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { DecimalInput } from "@/components/DecimalInput";
import { Alert, Badge, Button, Input } from "@/components/ui";
import {
  ApiRequestError,
  apiFetch,
  errorDetails,
  errorMessage,
  newIdempotencyKey,
} from "@/lib/api";
import { toDecimal } from "@/lib/decimal";
import { notifySuccess } from "@/lib/notice";
import { ORDER_KEY } from "../vendas/lib";
import { qty } from "../estoque/lib";
import {
  divergences,
  lineDifference,
  pendingEntries,
  returnLines,
  sameQuantity,
  type Entry,
  type ReturnLine,
} from "./acerto-lib";
import { LOAD_KEY } from "./lib";
import { PrestacaoRevisao } from "./PrestacaoRevisao";

type View = "retorno" | "conferencia" | "finalizar" | "finalizado";
const STEPS: { view: View; title: string }[] = [
  { view: "retorno", title: "Informar o retorno" },
  { view: "conferencia", title: "Confirmar a conferência" },
  { view: "finalizar", title: "Finalizar" },
];
const viewOf = (stage: SettlementStage): View =>
  stage === "finalizado" || stage === "cancelada"
    ? "finalizado"
    : stage === "finalizar"
      ? "finalizar"
      : stage === "conferencia"
        ? "conferencia"
        : "retorno";
const STEP_INDEX: Record<View, number> = {
  retorno: 0,
  conferencia: 1,
  finalizar: 2,
  finalizado: 3,
};

const MODE_LABEL: Record<EntryMode, string> = { digitado: "digitado", confere: "confere" };

/** O acerto mudou na API: guarda a resposta (sem esperar outra leitura) e avisa as listas. */
function useStoreLoad(id: string) {
  const queryClient = useQueryClient();
  return async (l: LoadDetail) => {
    queryClient.setQueryData([...LOAD_KEY, "detalhe", id], l);
    await queryClient.invalidateQueries({ queryKey: [...LOAD_KEY, "lista"] });
    await queryClient.invalidateQueries({ queryKey: ["estoque"] });
    await queryClient.invalidateQueries({ queryKey: ORDER_KEY });
  };
}

export function AcertoPanel({
  l,
  onShowReport,
  finishedExtra,
}: {
  l: LoadDetail;
  onShowReport: () => void;
  /** notas do acerto e estorno, mostrados depois de finalizado */
  finishedExtra?: ReactNode;
}) {
  const s = l.settlement;
  const [view, setView] = useState<View>(viewOf(s.stage));
  // a etapa avançou (ou voltou) na API: a tela acompanha
  useEffect(() => setView(viewOf(s.stage)), [s.stage, s.version]);
  const block = s.stage === "finalizado" ? null : s.nextAction.block;
  const reachable = STEP_INDEX[viewOf(s.stage)];

  return (
    <div className="space-y-5">
      <nav aria-label="Etapas do acerto" className="grid gap-3 sm:grid-cols-3">
        {STEPS.map((st, i) => {
          const done = reachable > i;
          const current = view === st.view;
          const canGo = !current && i <= reachable && s.stage !== "finalizado";
          const inner = (
            <>
              <span
                aria-hidden
                className={`flex size-7 shrink-0 items-center justify-center rounded-full text-sm font-bold ${
                  current
                    ? "bg-caramel text-white"
                    : done
                      ? "bg-success text-white"
                      : "bg-muted text-muted-foreground"
                }`}
              >
                {done && !current ? "✓" : i + 1}
              </span>
              <span className="min-w-0">
                <span
                  className={`block font-semibold ${current || done ? "" : "text-muted-foreground"}`}
                >
                  {st.title}
                </span>
                <span className="block text-xs text-muted-foreground">
                  {current ? "Você está aqui" : done ? "Concluída" : "Depois da anterior"}
                </span>
              </span>
            </>
          );
          const cls = `flex items-center gap-3 rounded-xl bg-card px-4 py-3 text-left ${
            current ? "border-2 border-caramel" : "border"
          }`;
          return canGo ? (
            <button
              key={st.view}
              type="button"
              className={`${cls} hover:bg-muted`}
              onClick={() => setView(st.view)}
            >
              {inner}
            </button>
          ) : (
            <div key={st.view} className={cls} aria-current={current ? "step" : undefined}>
              {inner}
            </div>
          );
        })}
      </nav>

      {block !== null && <BlockNotice l={l} />}
      <PrestacaoRevisao l={l} />
      {s.checkStale && s.stage !== "finalizado" && (
        <Alert tone="warn">
          <strong>A carga mudou depois da conferência</strong> (venda, estorno ou carregamento).
          Revise o retorno e confirme a conferência de novo.
        </Alert>
      )}

      {view === "retorno" && (
        <ReturnStep
          key={`${s.id ?? "novo"}-${s.version ?? 0}`}
          l={l}
          readOnly={block !== null && s.nextAction.stage === "retorno"}
          onReview={() => setView("conferencia")}
        />
      )}
      {view === "conferencia" && (
        <CheckStep
          l={l}
          readOnly={block !== null && s.nextAction.stage === "conferencia"}
          onBack={() => setView("retorno")}
        />
      )}
      {view === "finalizar" && (
        <FinishStep l={l} readOnly={block !== null} onBack={() => setView("conferencia")} />
      )}
      {view === "finalizado" && (
        <FinishedStep l={l} onShowReport={onShowReport} extra={finishedExtra} />
      )}
    </div>
  );
}

/** Quem é vendedor da carga (ou não tem a permissão) vê o porquê e quem pode continuar. */
function BlockNotice({ l }: { l: LoadDetail }) {
  const a = l.settlement.nextAction;
  const title =
    a.block === "vendedor_da_carga"
      ? "Você é o vendedor desta carga"
      : a.block === "declarante"
        ? "Você enviou a prestação de contas desta carga"
        : a.block === "quem_confirmou"
          ? "Você confirmou a conferência desta carga"
          : a.block === "prestacao_desatualizada"
            ? "A prestação de contas precisa ser revisada"
            : a.block === "revisor_da_prestacao"
              ? "Você revisou a prestação de contas desta carga"
              : "Esta etapa é de outra pessoa";
  const body =
    a.block === "vendedor_da_carga"
      ? "Por segurança, quem vendeu não confere nem finaliza o próprio acerto — vale também para administradores, e continua valendo se o vínculo com o vendedor for desfeito."
      : a.block === "declarante"
        ? "Quem enviou a prestação de contas não confirma a conferência."
        : a.block === "quem_confirmou"
          ? "Carga aberta antes do acerto simplificado, sem registro confiável do vendedor: quem confirmou a conferência não finaliza."
          : a.block === "sem_permissao_liberar"
            ? "O acerto tem divergência e a finalização exige a liberação de quem tem a permissão “Aprovar divergência de acerto”."
            : a.block === "prestacao_desatualizada"
              ? a.message
              : a.block === "revisor_da_prestacao"
                ? "Por segurança, quem revisou a prestação de contas pelo escritório não informa o retorno, não confere nem finaliza este acerto — vale também para administradores."
                : "Você não tem a permissão desta etapa.";
  return (
    <section
      role="status"
      className="flex gap-4 rounded-2xl border-2 border-info bg-card p-5"
      aria-label="Quem pode continuar"
    >
      <span
        aria-hidden
        className="flex size-11 shrink-0 items-center justify-center rounded-full bg-info-soft text-info"
      >
        <svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <rect x="5" y="11" width="14" height="10" rx="2" />
          <path d="M8 11V7a4 4 0 0 1 8 0v4" />
        </svg>
      </span>
      <div className="space-y-1.5">
        <h3 className="text-[17px] font-semibold">{title}</h3>
        <p className="text-sm">{body}</p>
        {a.whoCan && (
          <p className="text-sm text-muted-foreground">Quem pode continuar: {a.whoCan}</p>
        )}
      </div>
    </section>
  );
}

// ---------------------------------------------------------------- 1. retorno
function ReturnStep({
  l,
  readOnly,
  onReview,
}: {
  l: LoadDetail;
  readOnly: boolean;
  onReview: () => void;
}) {
  const s = l.settlement;
  const lines = returnLines(s);
  const sent = s.declaredAt !== null;
  const [counts, setCounts] = useState<Record<string, Entry>>(() =>
    Object.fromEntries(
      lines.map((ln) => [
        ln.key,
        { value: toFormNumber(ln.counted), mode: ln.countedMode ?? "digitado" },
      ]),
    ),
  );
  const [money, setMoney] = useState<Record<string, Entry>>(() =>
    Object.fromEntries(
      s.payments.map((p) => [
        p.paymentMethodId,
        { value: toFormMoney(p.delivered), mode: p.deliveredMode ?? "digitado" },
      ]),
    ),
  );
  const store = useStoreLoad(l.id);
  const save = useMutation({
    mutationFn: () =>
      apiFetch<LoadDetail>(`/cargas/${l.id}/acerto/retorno`, {
        method: "PUT",
        body: {
          version: s.version,
          counts: lines.map((ln) => {
            const e = counts[ln.key] ?? { value: "", mode: "digitado" as const };
            return {
              productId: ln.productId,
              lotId: ln.lotId,
              counted: e.value.trim() === "" ? null : toDecimal(e.value),
              mode: e.mode,
            };
          }),
          payments: s.payments.map((p) => {
            const e = money[p.paymentMethodId] ?? { value: "", mode: "digitado" as const };
            return {
              paymentMethodId: p.paymentMethodId,
              delivered: e.value.trim() === "" ? null : toDecimal(e.value, "dinheiro"),
              mode: e.mode,
            };
          }),
        },
      }),
  });
  const pending = pendingEntries(s, counts, money);
  const dirty =
    lines.some((ln) => {
      const v = (counts[ln.key]?.value ?? "").trim();
      return !sameQuantity(v === "" ? null : toDecimal(v), ln.counted);
    }) ||
    s.payments.some((p) => {
      const v = (money[p.paymentMethodId]?.value ?? "").trim();
      return !sameQuantity(v === "" ? null : toDecimal(v, "dinheiro"), p.delivered);
    });

  const inFlight = useRef(false);
  async function submit(next: boolean) {
    if (!dirty) {
      if (next) onReview();
      return;
    }
    if (inFlight.current) return;
    inFlight.current = true;
    let r: LoadDetail;
    try {
      r = await save.mutateAsync();
    } catch {
      return; // o erro aparece abaixo dos botões
    } finally {
      inFlight.current = false;
    }
    await store(r);
    notifySuccess(next ? "Retorno salvo. Revise a conferência." : "Retorno salvo como rascunho.");
    if (next && r.settlement.stage !== "retorno") onReview();
  }

  const setCount = (key: string, e: Entry) => setCounts((c) => ({ ...c, [key]: e }));
  const setPay = (id: string, e: Entry) => setMoney((m) => ({ ...m, [id]: e }));

  return (
    <div className="space-y-5">
      <Alert tone="info">
        Conte a mercadoria e o dinheiro que o vendedor entregou e informe{" "}
        <strong>o que você contou</strong>. O esperado é só referência: nenhuma linha é preenchida
        sozinha. Se bater, use “Confere” na linha.
        {s.origin !== null && <> · {SETTLEMENT_ORIGIN_LABEL[s.origin]}</>}
      </Alert>

      <section aria-label="Mercadoria devolvida" className="rounded-xl border bg-card shadow-card">
        <header className="flex flex-wrap items-center justify-between gap-2 px-5 py-4">
          <h3 className="text-[17px] font-semibold">Mercadoria devolvida (contagem física)</h3>
          <span className="text-sm text-muted-foreground">por produto e lote</span>
        </header>
        {lines.length === 0 ? (
          <p className="px-5 pb-5 text-sm text-muted-foreground">Nada foi carregado nesta carga.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[820px] text-left text-sm">
              <thead className="bg-table-head text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-4 py-3">Produto / lote</th>
                  <th className="px-3 py-3 text-right">Carregado</th>
                  <th className="px-3 py-3 text-right">Vendido</th>
                  {sent && <th className="px-3 py-3 text-right">Declarado</th>}
                  <th className="px-3 py-3 text-right">Esperado (referência)</th>
                  <th className="px-3 py-3">Contado</th>
                  <th className="px-4 py-3 text-right">Diferença</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/70">
                {lines.map((ln) => (
                  <CountRow
                    key={ln.key}
                    ln={ln}
                    entry={counts[ln.key] ?? { value: "", mode: "digitado" }}
                    showDeclared={sent}
                    readOnly={readOnly}
                    onChange={(e) => setCount(ln.key, e)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section aria-label="Dinheiro entregue" className="rounded-xl border bg-card shadow-card">
        <header className="flex flex-wrap items-center justify-between gap-2 px-5 py-4">
          <h3 className="text-[17px] font-semibold">Dinheiro entregue (conferido)</h3>
          <span className="text-sm text-muted-foreground">por forma de pagamento</span>
        </header>
        {s.payments.length === 0 ? (
          <p className="px-5 pb-5 text-sm text-muted-foreground">
            Nenhum recebimento à vista nas vendas desta carga.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] text-left text-sm">
              <thead className="bg-table-head text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-4 py-3">Forma</th>
                  <th className="px-3 py-3">Quem recebe</th>
                  {sent && <th className="px-3 py-3 text-right">Declarado</th>}
                  <th className="px-3 py-3 text-right">Esperado (referência)</th>
                  <th className="px-3 py-3">Conferido</th>
                  <th className="px-4 py-3 text-right">Diferença</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/70">
                {s.payments.map((p) => {
                  const e = money[p.paymentMethodId] ?? { value: "", mode: "digitado" as const };
                  const v = e.value.trim() === "" ? null : toDecimal(e.value, "dinheiro");
                  let diff: string | null = null;
                  try {
                    diff = v === null ? null : formatMoney(Number(v) - Number(p.expected));
                  } catch {
                    diff = null;
                  }
                  const c = v === null ? null : compareMoney(v, p.expected);
                  const saved = p.delivered !== null && sameQuantity(v, p.delivered);
                  return (
                    <tr
                      key={p.paymentMethodId}
                      className={c !== null && c !== 0 ? "bg-destructive-soft/40" : undefined}
                    >
                      <td className="px-4 py-3 font-semibold">{p.paymentMethodName}</td>
                      <td className="px-3 py-3 text-muted-foreground">
                        {PAYMENT_HANDLING_LABEL[p.handling]}
                      </td>
                      {sent && (
                        <td className="px-3 py-3 text-right">
                          {p.declared === null ? "—" : formatMoney(p.declared)}
                        </td>
                      )}
                      <td className="px-3 py-3 text-right text-muted-foreground">
                        {formatMoney(p.expected)}
                      </td>
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-2">
                          <DecimalInput
                            aria-label={`Conferido em ${p.paymentMethodName}`}
                            className="w-32 text-right"
                            mode="dinheiro"
                            value={e.value}
                            disabled={readOnly}
                            placeholder="R$ 0,00"
                            onChange={(ev) =>
                              setPay(p.paymentMethodId, {
                                value: ev.target.value,
                                mode: "digitado",
                              })
                            }
                          />
                          {!readOnly && (
                            <Button
                              size="sm"
                              variant="secondary"
                              onClick={() =>
                                setPay(p.paymentMethodId, {
                                  value: toFormMoney(p.expected),
                                  mode: "confere",
                                })
                              }
                            >
                              Confere: {formatMoney(p.expected)}
                            </Button>
                          )}
                        </div>
                        <EntryStatus
                          empty={v === null}
                          saved={saved}
                          mode={saved ? p.deliveredMode : e.mode}
                          byEmail={saved ? p.deliveredByEmail : null}
                          differs={c !== null && c !== 0}
                        />
                      </td>
                      <td className="px-4 py-3 text-right">
                        {c === null ? (
                          <span className="text-muted-foreground">—</span>
                        ) : c === 0 ? (
                          <span className="font-semibold text-success">Confere</span>
                        ) : (
                          <Badge tone="error">
                            {c < 0 ? `Falta ${diff?.replace("-", "")}` : `Sobra ${diff}`}
                          </Badge>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {!readOnly && (
        <div className="sticky bottom-0 z-10 flex flex-wrap items-center justify-between gap-3 rounded-xl border bg-card px-5 py-4 shadow-pop">
          <p className="text-sm text-muted-foreground">
            {pending.length > 0 ? (
              <>
                Faltam <strong className="text-foreground">{pending.length} linha(s)</strong>:{" "}
                {pending.join(", ")}.
              </>
            ) : dirty ? (
              "Tudo informado. Salve para revisar a conferência."
            ) : (
              "Tudo informado e salvo."
            )}
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="secondary"
              disabled={!dirty || save.isPending}
              onClick={() => void submit(false)}
            >
              Salvar rascunho
            </Button>
            <Button
              disabled={pending.length > 0 || save.isPending}
              onClick={() => void submit(true)}
            >
              {save.isPending ? "Salvando..." : "Salvar e revisar →"}
            </Button>
          </div>
          {pending.length > 0 && (
            <p className="w-full text-xs text-muted-foreground">
              Informe todas as linhas para continuar (zero também é uma contagem).
            </p>
          )}
          {save.error && <ErrorBox error={save.error} />}
        </div>
      )}
    </div>
  );
}

function CountRow({
  ln,
  entry,
  showDeclared,
  readOnly,
  onChange,
}: {
  ln: ReturnLine;
  entry: Entry;
  showDeclared: boolean;
  readOnly: boolean;
  onChange: (e: Entry) => void;
}) {
  const v = entry.value.trim() === "" ? null : toDecimal(entry.value);
  const diff = lineDifference(ln.expected, v);
  const d = diff === null ? null : toScaled(diff);
  const saved = ln.counted !== null && sameQuantity(v, ln.counted);
  return (
    <tr className={d !== null && d !== 0n ? "bg-destructive-soft/40" : undefined}>
      <td className="px-4 py-3">
        <div className="font-semibold">
          <span className="font-mono text-xs">{ln.productCode}</span> · {ln.productName}
        </div>
        <div className="text-xs text-muted-foreground">
          {ln.lotCode ? `Lote ${ln.lotCode}` : "Sem lote"}
          {ln.lotExpiresOn ? ` · val. ${ln.lotExpiresOn.split("-").reverse().join("/")}` : ""}
        </div>
      </td>
      <td className="px-3 py-3 text-right">{qty(ln.loaded, ln.unit)}</td>
      <td className="px-3 py-3 text-right">{qty(ln.sold)}</td>
      {showDeclared && <td className="px-3 py-3 text-right">{qty(ln.declared)}</td>}
      <td className="px-3 py-3 text-right text-muted-foreground">{qty(ln.expected)}</td>
      <td className="px-3 py-3">
        <div className="flex items-center gap-2">
          <DecimalInput
            aria-label={`Contado ${ln.productCode}${ln.lotCode ? ` lote ${ln.lotCode}` : ""}`}
            className="w-28 text-right"
            mode="numero"
            value={entry.value}
            disabled={readOnly}
            placeholder="Informe"
            onChange={(e) => onChange({ value: e.target.value, mode: "digitado" })}
          />
          {!readOnly && (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => onChange({ value: toFormNumber(ln.expected) || "0", mode: "confere" })}
            >
              Confere: {qty(ln.expected)}
            </Button>
          )}
        </div>
        <EntryStatus
          empty={v === null}
          optional={ln.optional}
          saved={saved}
          mode={saved ? ln.countedMode : entry.mode}
          byEmail={saved ? ln.countedByEmail : null}
          differs={d !== null && d !== 0n}
        />
      </td>
      <td className="px-4 py-3 text-right">
        {d === null ? (
          <span className="text-muted-foreground">—</span>
        ) : d === 0n ? (
          <span className="font-semibold text-success">Confere</span>
        ) : (
          <Badge tone="error">
            {d > 0n ? `Falta ${qty(diff)}` : `Sobra ${qty(diff?.replace("-", "") ?? "0")}`}
          </Badge>
        )}
      </td>
    </tr>
  );
}

/** Situação da linha: pendente, alterada (não salva) ou contada por quem e como. */
function EntryStatus({
  empty,
  optional = false,
  saved,
  mode,
  byEmail,
  differs,
}: {
  empty: boolean;
  optional?: boolean;
  saved: boolean;
  mode: EntryMode | null;
  byEmail: string | null;
  differs: boolean;
}) {
  if (empty) {
    return (
      <p className={`mt-1 text-xs ${optional ? "text-muted-foreground" : "text-warning-strong"}`}>
        {optional ? "Sem movimento (opcional)" : "Pendente"}
      </p>
    );
  }
  if (!saved) return <p className="mt-1 text-xs text-info">Alterado — salve para gravar</p>;
  return (
    <p className={`mt-1 text-xs ${differs ? "text-destructive" : "text-success"}`}>
      Contado{byEmail ? ` por ${byEmail}` : ""}
      {mode ? ` · ${MODE_LABEL[mode]}` : ""}
    </p>
  );
}

// ---------------------------------------------------------------- 2. conferência
function CheckStep({
  l,
  readOnly,
  onBack,
}: {
  l: LoadDetail;
  readOnly: boolean;
  onBack: () => void;
}) {
  const s = l.settlement;
  const div = divergences(s);
  const [reason, setReason] = useState(s.checkReason ?? "");
  const [key, setKey] = useState(newIdempotencyKey);
  const store = useStoreLoad(l.id);
  const confirm = useMutation({
    mutationFn: () =>
      apiFetch<LoadDetail>(`/cargas/${l.id}/acerto/conferencia/confirmar`, {
        method: "POST",
        idempotencyKey: key,
        body: {
          version: s.version,
          fingerprint: s.fingerprint,
          checkReason: reason.trim() === "" ? null : reason.trim(),
        },
      }),
    onSuccess: async (r) => {
      setKey(newIdempotencyKey());
      await store(r);
      notifySuccess("Conferência confirmada.");
    },
  });
  const inFlight = useRef(false);
  const submit = () => {
    if (inFlight.current) return;
    inFlight.current = true;
    confirm.mutate(undefined, { onSettled: () => (inFlight.current = false) });
  };
  const reasonOk = !s.hasDivergence || reason.trim().length >= SETTLEMENT_REASON_MIN;
  const lines = returnLines(s).filter((ln) => !ln.optional || ln.counted !== null);

  return (
    <div className="space-y-5">
      {s.stage === "retorno" && (
        <Alert tone="warn">
          Ainda há linhas sem valor conferido: volte ao retorno e informe todas.
        </Alert>
      )}
      <section aria-label="Divergências" className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <DivergenceCard
          title="Falta de mercadoria"
          empty={div.shortage.length === 0}
          value={div.shortage.map((x) => `${qty(x.quantity)} ${x.unit}`).join(" + ")}
          hint={div.shortage.map((x) => x.label).join("; ")}
          extra={div.shortage.length > 0 ? `${formatMoney(div.shortageValue)} a custo` : undefined}
        />
        <DivergenceCard
          title="Sobra de mercadoria"
          empty={div.surplus.length === 0}
          value={div.surplus.map((x) => `${qty(x.quantity)} ${x.unit}`).join(" + ")}
          hint={div.surplus.map((x) => x.label).join("; ")}
          extra={div.surplus.length > 0 ? `${formatMoney(div.surplusValue)} a custo` : undefined}
        />
        <DivergenceCard
          title="Falta de dinheiro"
          empty={div.cashShortage.length === 0}
          value={formatMoney(div.cashShortageTotal)}
          hint={div.cashShortage.map((x) => `${x.label}: ${formatMoney(x.amount)}`).join("; ")}
        />
        <DivergenceCard
          title="Sobra de dinheiro"
          empty={div.cashSurplus.length === 0}
          value={formatMoney(div.cashSurplusTotal)}
          hint={div.cashSurplus.map((x) => `${x.label}: ${formatMoney(x.amount)}`).join("; ")}
        />
      </section>
      {s.checkDivergence && (
        <Alert tone="warn">
          O conferido difere do que o vendedor declarou na prestação de contas
          {s.checkedByDeclarant ? " (ou foi informado por quem declarou)" : ""}. O declarado fica
          preservado e a diferença precisa de justificativa.
        </Alert>
      )}

      <section aria-label="Esperado e conferido" className="rounded-xl border bg-card shadow-card">
        <header className="px-5 py-4">
          <h3 className="text-[17px] font-semibold">Esperado × conferido</h3>
        </header>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] text-left text-sm">
            <thead className="bg-table-head text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-4 py-3">Item</th>
                <th className="px-3 py-3 text-right">Esperado</th>
                <th className="px-3 py-3 text-right">Conferido</th>
                <th className="px-4 py-3 text-right">Diferença</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/70">
              {lines.map((ln) => {
                const diff = lineDifference(ln.expected, ln.counted);
                const d = diff === null ? null : toScaled(diff);
                return (
                  <tr
                    key={ln.key}
                    className={d !== null && d !== 0n ? "bg-destructive-soft/40" : undefined}
                  >
                    <td className="px-4 py-3">
                      <strong>{ln.productCode}</strong> {ln.lotCode ? `· lote ${ln.lotCode}` : ""}
                    </td>
                    <td className="px-3 py-3 text-right">{qty(ln.expected, ln.unit)}</td>
                    <td className="px-3 py-3 text-right">{qty(ln.counted, ln.unit)}</td>
                    <td className="px-4 py-3 text-right">
                      <DiffLabel
                        q={d}
                        text={diff === null ? null : qty(diff.replace("-", ""), ln.unit)}
                      />
                    </td>
                  </tr>
                );
              })}
              {s.payments.map((p) => {
                const c = p.delivered === null ? null : compareMoney(p.delivered, p.expected);
                return (
                  <tr
                    key={p.paymentMethodId}
                    className={c !== null && c !== 0 ? "bg-destructive-soft/40" : undefined}
                  >
                    <td className="px-4 py-3">
                      <strong>{p.paymentMethodName}</strong> ·{" "}
                      {PAYMENT_HANDLING_LABEL[p.handling].toLowerCase()}
                    </td>
                    <td className="px-3 py-3 text-right">{formatMoney(p.expected)}</td>
                    <td className="px-3 py-3 text-right">{formatMoney(p.delivered)}</td>
                    <td className="px-4 py-3 text-right">
                      <DiffLabel
                        q={c === null ? null : c < 0 ? 1n : c > 0 ? -1n : 0n}
                        text={
                          p.difference === null ? null : formatMoney(p.difference.replace("-", ""))
                        }
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {!readOnly && (
        <section
          aria-label="Justificativa"
          className="space-y-2 rounded-xl border bg-card p-5 shadow-card"
        >
          <label htmlFor="justificativa" className="font-semibold">
            Justificativa da divergência{" "}
            {s.hasDivergence ? (
              <span className="text-destructive">(obrigatória)</span>
            ) : (
              <span className="text-muted-foreground">(opcional: nada diverge)</span>
            )}
          </label>
          <textarea
            id="justificativa"
            rows={3}
            value={reason}
            maxLength={500}
            onChange={(e) => setReason(e.target.value)}
            placeholder="O que aconteceu? Ex.: pacote avariado na rota; troco dado a mais."
            className="w-full rounded-lg border border-input bg-background px-3 py-2 text-[15px]"
          />
          <p className="text-xs text-muted-foreground">
            {s.hasDivergence
              ? `Pelo menos ${SETTLEMENT_REASON_MIN} caracteres (${reason.trim().length}). Fica no acerto com o seu nome; a finalização vai exigir a liberação de quem tem a permissão “Aprovar divergência de acerto”.`
              : "Fica registrada no acerto com o seu nome."}
          </p>
        </section>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border bg-card px-5 py-4 shadow-card">
        <Button variant="ghost" onClick={onBack}>
          ← Corrigir o retorno
        </Button>
        {!readOnly && (
          <Button
            disabled={!reasonOk || confirm.isPending || s.stage === "retorno"}
            onClick={submit}
          >
            {confirm.isPending ? "Confirmando..." : "Confirmar conferência"}
          </Button>
        )}
        {!readOnly && !reasonOk && (
          <p className="w-full text-xs text-destructive">
            Escreva a justificativa (pelo menos {SETTLEMENT_REASON_MIN} caracteres) para confirmar.
          </p>
        )}
        {confirm.error && <ErrorBox error={confirm.error} />}
      </div>
    </div>
  );
}

function DiffLabel({ q, text }: { q: bigint | null; text: string | null }) {
  if (q === null) return <span className="text-muted-foreground">—</span>;
  if (q === 0n) return <span className="font-semibold text-success">Confere</span>;
  return <Badge tone="error">{q > 0n ? `Falta ${text}` : `Sobra ${text}`}</Badge>;
}

function DivergenceCard({
  title,
  empty,
  value,
  hint,
  extra,
}: {
  title: string;
  empty: boolean;
  value: string;
  hint: string;
  extra?: string | undefined;
}) {
  return (
    <div
      className={`rounded-xl border p-4 ${empty ? "bg-card" : "border-destructive/30 bg-destructive-soft"}`}
    >
      <div
        className={`text-sm font-semibold ${empty ? "text-muted-foreground" : "text-destructive"}`}
      >
        {title}
      </div>
      <div className={`text-2xl font-bold ${empty ? "text-muted-foreground" : "text-destructive"}`}>
        {empty ? "Nenhuma" : value}
      </div>
      <div className={`text-xs ${empty ? "text-muted-foreground" : "text-destructive"}`}>
        {empty ? "—" : [hint, extra].filter(Boolean).join(" · ")}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- 3. finalização
function FinishStep({
  l,
  readOnly,
  onBack,
}: {
  l: LoadDetail;
  readOnly: boolean;
  onBack: () => void;
}) {
  const s = l.settlement;
  const div = divergences(s);
  const queryClient = useQueryClient();
  const store = useStoreLoad(l.id);
  const [release, setRelease] = useState(false);
  const [reason, setReason] = useState(s.checkReason ?? "");
  // uma chave por intenção: duplo clique e reenvio depois de queda usam a mesma
  const [key] = useState(newIdempotencyKey);
  const [checking, setChecking] = useState(false);
  // o segundo clique chega antes de a tela trocar o botão: ignora enquanto a primeira está no ar
  const inFlight = useRef(false);
  const finish = () => {
    if (inFlight.current) return;
    inFlight.current = true;
    close.mutate(undefined, { onSettled: () => (inFlight.current = false) });
  };
  const close = useMutation({
    mutationFn: () =>
      apiFetch<LoadDetail>(`/cargas/${l.id}/acerto/fechar`, {
        method: "POST",
        idempotencyKey: key,
        body: {
          version: s.version,
          fingerprint: s.fingerprint,
          releaseDivergence: s.hasDivergence && release,
          divergenceReason: s.hasDivergence ? reason.trim() : null,
        },
      }),
    onSuccess: async (r) => {
      await store(r);
      notifySuccess(`Acerto ${r.settlement.number ?? ""} finalizado.`);
    },
    onError: async (err) => {
      // sem resposta (conexão caiu, servidor fora): confere na API se a finalização aconteceu
      const uncertain = !(err instanceof ApiRequestError) || err.status >= 500;
      if (!uncertain) return;
      setChecking(true);
      try {
        const fresh = await apiFetch<LoadDetail>(`/cargas/${l.id}`);
        if (fresh.settlement.stage === "finalizado") await store(fresh);
      } catch {
        /* continua sem resposta: a tela oferece tentar de novo (mesma chave) */
      } finally {
        setChecking(false);
      }
      await queryClient.invalidateQueries({ queryKey: [...LOAD_KEY, "detalhe", l.id] });
    },
  });
  const returned = returnLines(s).filter((ln) => ln.counted !== null && toScaled(ln.counted) > 0n);
  const releaseOk = !s.hasDivergence || (release && reason.trim().length >= SETTLEMENT_REASON_MIN);
  const uncertain =
    close.error !== null &&
    (!(close.error instanceof ApiRequestError) || close.error.status >= 500);

  return (
    <div className="space-y-5">
      <section
        aria-label="O que vai acontecer"
        className="space-y-4 rounded-xl border bg-card p-5 shadow-card"
      >
        <h3 className="text-[19px] font-semibold">Ao finalizar, isto acontece de uma vez só</h3>
        <div className="grid gap-4 md:grid-cols-3">
          <div className="rounded-lg border p-4">
            <h4 className="mb-2 font-semibold">Estoque</h4>
            <ul className="ml-4 list-disc space-y-1 text-sm">
              {returned.length > 0 && (
                <li>
                  Voltam para <strong>{l.originWarehouseName}</strong>:{" "}
                  {returned
                    .map(
                      (ln) =>
                        `${ln.productCode}${ln.lotCode ? ` lote ${ln.lotCode}` : ""} ${qty(ln.counted, ln.unit)}`,
                    )
                    .join("; ")}
                </li>
              )}
              {div.shortage.map((x) => (
                <li key={`f-${x.label}`}>
                  Falta de {qty(x.quantity)} {x.unit} ({x.label}): baixada do estoque do vendedor e
                  registrada
                  {x.value ? ` (${formatMoney(x.value)} a custo)` : ""}
                </li>
              ))}
              {div.surplus.map((x) => (
                <li key={`s-${x.label}`}>
                  Sobra de {qty(x.quantity)} {x.unit} ({x.label}): lançada e devolvida à fábrica
                </li>
              ))}
              <li>Estoque de {l.salespersonName} fica zerado</li>
            </ul>
          </div>
          <div className="rounded-lg border p-4">
            <h4 className="mb-2 font-semibold">Dinheiro</h4>
            <ul className="ml-4 list-disc space-y-1 text-sm">
              {s.payments.length === 0 && <li>Nenhum recebimento à vista nesta carga</li>}
              {s.payments.map((p) => (
                <li key={p.paymentMethodId}>
                  {p.paymentMethodName}: {formatMoney(p.delivered)} conferido de{" "}
                  {formatMoney(p.expected)} esperado
                </li>
              ))}
              {(div.cashShortage.length > 0 || div.cashSurplus.length > 0) && (
                <li>
                  Diferença registrada no acerto e no relatório (sem título nem desconto de
                  comissão)
                </li>
              )}
            </ul>
          </div>
          <div className="rounded-lg border p-4">
            <h4 className="mb-2 font-semibold">Carga</h4>
            <ul className="ml-4 list-disc space-y-1 text-sm">
              <li>
                {l.number} passa a <strong>Acertada</strong>
              </li>
              <li>Novas vendas nesta carga ficam bloqueadas (ERP e aplicativo)</li>
              <li>
                Notas de retorno, falta e sobra ficam disponíveis depois, quando a operação exigir
              </li>
            </ul>
          </div>
        </div>
      </section>

      {s.hasDivergence && (
        <section
          aria-label="Liberação da divergência"
          className="space-y-3 rounded-xl border border-warning/40 bg-warning-soft p-5"
        >
          <p className="font-semibold text-warning-strong">Este acerto tem divergência</p>
          {s.checkReason && (
            <p className="text-sm text-warning-strong">
              Justificativa da conferência: “{s.checkReason}”
              {s.checkedByEmail ? ` (${s.checkedByEmail})` : ""}
            </p>
          )}
          {!readOnly && (
            <>
              <label className="flex min-h-11 items-center gap-3 text-[15px]">
                <input
                  type="checkbox"
                  className="size-5"
                  checked={release}
                  onChange={(e) => setRelease(e.target.checked)}
                />
                Libero a divergência (fica registrado com o meu usuário)
              </label>
              <label htmlFor="motivo-liberacao" className="block text-sm font-semibold">
                Motivo da liberação (pelo menos {SETTLEMENT_REASON_MIN} caracteres)
              </label>
              <Input
                id="motivo-liberacao"
                value={reason}
                maxLength={500}
                onChange={(e) => setReason(e.target.value)}
              />
            </>
          )}
        </section>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border bg-card px-5 py-4 shadow-card">
        <Button variant="ghost" onClick={onBack} disabled={close.isPending}>
          ← Voltar à conferência
        </Button>
        {!readOnly && (
          <div className="flex flex-wrap items-center gap-3">
            <span className="max-w-sm text-xs text-muted-foreground">
              Depois de finalizado, só um estorno com permissão desfaz o acerto.
            </span>
            <Button size="lg" disabled={!releaseOk || close.isPending || checking} onClick={finish}>
              {close.isPending
                ? "Finalizando..."
                : uncertain
                  ? "Tentar de novo"
                  : s.hasDivergence
                    ? "Liberar e finalizar acerto"
                    : "Finalizar acerto"}
            </Button>
          </div>
        )}
        {checking && (
          <Alert tone="info">
            Não recebemos a resposta. Verificando se o acerto foi finalizado…
          </Alert>
        )}
        {uncertain && !checking && (
          <Alert tone="warn">
            Não recebemos a resposta da finalização. Nada foi feito pela metade: tente de novo — o
            sistema reconhece a mesma solicitação e não finaliza duas vezes.
          </Alert>
        )}
        {close.error && !uncertain && <ErrorBox error={close.error} />}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- finalizado
function FinishedStep({
  l,
  onShowReport,
  extra,
}: {
  l: LoadDetail;
  onShowReport: () => void;
  extra?: ReactNode;
}) {
  const s = l.settlement;
  if (s.stage === "cancelada") return <Alert tone="warn">Carga cancelada: não há acerto.</Alert>;
  const div = divergences(s);
  const returned = returnLines(s).reduce(
    (acc, ln) => acc + (ln.counted === null ? 0 : Number(ln.counted)),
    0,
  );
  return (
    <div className="space-y-5">
      <section
        role="status"
        className="flex gap-4 rounded-2xl border border-success/30 bg-success-soft p-6"
      >
        <span
          aria-hidden
          className="flex size-12 shrink-0 items-center justify-center rounded-full bg-success text-white"
        >
          <svg
            width="26"
            height="26"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
          >
            <path d="M5 12l5 5 9-10" />
          </svg>
        </span>
        <div className="space-y-1">
          <h3 className="text-[22px] font-semibold text-success-strong">
            Acerto {s.number} finalizado
          </h3>
          <p className="text-[15px] text-success-strong">
            {s.confirmedByEmail ? `Finalizado por ${s.confirmedByEmail}` : "Finalizado"}
            {s.confirmedAt
              ? ` em ${new Date(s.confirmedAt).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" })}`
              : ""}
            . {s.divergenceReleasedByEmail ? "Divergência liberada com motivo. " : ""}A carga está
            encerrada para novas vendas.
          </p>
        </div>
      </section>
      <section aria-label="Resultado" className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <ResultCard
          label="Devolvido à fábrica"
          value={qty(String(returned))}
          hint={l.originWarehouseName}
        />
        <ResultCard
          label="Diferença de mercadoria"
          value={formatMoney(s.goodsDifferenceValue)}
          hint={
            div.shortage.length + div.surplus.length > 0 ? "a custo (falta + sobra)" : "nenhuma"
          }
        />
        <ResultCard
          label="Diferença de dinheiro"
          value={formatMoney(s.cashDifference)}
          hint="conferido − esperado"
        />
        <ResultCard
          label="Origem"
          value={s.origin ? SETTLEMENT_ORIGIN_LABEL[s.origin] : "—"}
          hint={s.checkedByEmail ? `conferido por ${s.checkedByEmail}` : ""}
        />
      </section>
      <div className="flex flex-wrap gap-3">
        <Button onClick={onShowReport}>Ver relatório do acerto</Button>
      </div>
      {extra}
    </div>
  );
}

function ResultCard({ label, value, hint }: { label: string; value: string; hint: string }) {
  return (
    <div className="rounded-xl border bg-card p-4 shadow-card">
      <div className="text-sm text-muted-foreground">{label}</div>
      <div className="text-2xl font-bold">{value}</div>
      <div className="text-xs text-muted-foreground">{hint}</div>
    </div>
  );
}

function ErrorBox({ error }: { error: unknown }) {
  const details = errorDetails(error);
  return (
    <div className="w-full">
      <Alert tone="error">
        {errorMessage(error)}
        {details.length > 0 && (
          <ul className="mt-1 ml-5 list-disc">
            {details.map((d) => (
              <li key={d}>{d}</li>
            ))}
          </ul>
        )}
      </Alert>
    </div>
  );
}
